// TODO: where possible, conform to naming convention (in FE-specific/non-BE-interactive code)
import React, { useContext, useEffect, useState } from 'react';
import { CentralProviderContext, CentralProviderState, useTranslation } from '@michelin/central-provider';
import { CONTINUE_WITHOUT_PAYMENT_METHOD_OPTION } from 'compositions/OrderDetail/components/Payment/constants';
import { useAddPartsToOrder } from 'hooks/useAddPartsToOrder';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { useGoogleAnalytics } from 'hooks/useGoogleAnalytics';
import { AnalyticsAction, AnalyticsCategory } from 'hooks/useGoogleAnalytics/types';
import { CreateNoteParams } from 'hooks/useNoteHooks';
import { useOrderActions } from 'hooks/useOrderActions';
import { useOrderCreate } from 'hooks/useOrderCreate';
import cloneDeep from 'lodash/cloneDeep';
import { useHistory } from 'react-router-dom';
import { BillingPreferencesType } from 'types/BillingPreferencesType';
import {
  AdditionalServicesType,
  AppointmentType,
  CountriesType,
  LocationCore,
  LocationType,
  MobileAddress,
  UFOPart,
  VehicleType,
} from 'types/UFOTypes';
import { checkMasks } from 'utils/checkMask';
import { PQL_MOUNTING_WHEEL } from 'utils/constantValues';
import { formatAsLocationCoreType } from 'utils/format';
import { getMspn } from 'utils/getMspn';
import { insertElement } from 'utils/insertElementToArray';
import { useUFOPaths } from 'utils/paths';
import { TaxesType } from 'utils/taxes';
import { useEnqueueSnackbar } from 'hooks/useEnqueueSnackbar';

export type PaymentType = {
  credit_card_token?: string | null;
  credit_card_last_four?: string | null;
  po_number?: string | null;
  authorization_number?: string | null;
  reference_number?: string | null;
  payment_type?: string | null;
};

export interface SelectedServicesType {
  part: AdditionalServicesType;
  lines: {
    selected: boolean;
    index: number;
    quantity: number;
    comment: string;
    id: number | null;
    pql?: number;
    removed?: boolean;
    related_to?: string;
  }[];
  error?: string | null;
}

export interface PartPayload {
  part_id: string;
  quantity: number;
  comment?: string;
  part_description: string;
  related_to?: string;
  ispql: boolean;
  manual_pricing?: boolean;
  invoice_price?: number;
  final_invoice_price?: number;
  removed?: boolean;
}

export interface GetServicesPayload {
  part_id: string;
  quantity: number;
  comment?: string;
  part_description: string;
  ispql: boolean;
  line?: number;
  removed?: boolean;
  related_to?: string;
}

export interface InstallationType {
  mspn: string;
  quantity: number;
  related_to: string;
  removed: boolean;
  comment?: string;
  id?: number;
  isPql?: boolean;
  type?: string;
}

export interface SelectedTire {
  part: UFOPart;
  qty: number;
  removed?: boolean;
  related_to: string;
}

export interface GroupedTiresType {
  tireMspn: string;
  tires: {
    hash: string;
    qty: number;
    installations?: InstallationType[];
  }[];
}

type WizardContextType = {
  vehicle: VehicleType | null;
  setVehicle: (v: VehicleType | null) => void;
  selectedTires: SelectedTire[];
  setSelectedTires: React.Dispatch<React.SetStateAction<SelectedTire[]>>;
  selectedServices: SelectedServicesType[];
  setSelectedServices: React.Dispatch<React.SetStateAction<SelectedServicesType[]>>;
  provider: LocationCore | null;
  setProvider: (l: LocationCore | null) => void;
  fleet: LocationCore | null;
  setFleet: (l: LocationCore | null) => void;
  date: Date | null;
  setDate: (d: Date | null) => void;
  disabled: boolean;
  setDisabled: (d: boolean) => void;
  payment: PaymentType | null;
  setPayment: (payment: PaymentType | null) => void;
  authorized: boolean;
  setAuthorized: (a: boolean) => void;
  showAppointmentSelector: boolean;
  setShowAppointmentSelector: (a: boolean) => void;
  appointmentSelected: boolean;
  setAppointmentSelected: (a: boolean) => void;
  mobileAddress: MobileAddress | null;
  setMobileAddress: (a: MobileAddress | null) => void;
  customReq: { text: string; selected: boolean };
  setCustomReq: (cr: { text: string; selected: boolean }) => void;
  appointmentType: AppointmentType | null;
  setAppointmentType: (at: AppointmentType | null) => void;
  contextPqlPrices?: { mspn: string; price: number; line: number; tireMspn: string }[];
  setContextPqlPrices: (parts: { mspn: string; price: number; line: number; tireMspn: string }[]) => void;
  totalPrice?: number;
  setTotalPrice: (v: number) => void;
  deliveryTaxes?: { taxes: TaxesType; total: number };
  setDeliveryTaxes: (param: { taxes: TaxesType; total: number }) => void;
  billingPreferences: BillingPreferencesType | undefined;
  setBillingPreferences: (bp: BillingPreferencesType | undefined) => void;
  getItemsQty: () => number;
  loading: boolean;
  showCartModal: boolean;
  setShowCartModal: (v: boolean) => void;
  getServices: (withCustom?: boolean) => GetServicesPayload[];
  getPartAndServices: () => PartPayload[];
  createOrder: () => void;
  setPql: (mspn: string, price: number, line: number, tireMspn: string) => void;
  getPql: (mspn: string, line: number, tireMspn: string) => number | null;
  getCommonPricesParams: () => {
    mode: LocationType;
    provider_id: string;
    fleet_id: string;
    country_code?: CountriesType;
    state?: string;
    city?: string;
    zip_code?: string;
  };
  note?: CreateNoteParams;
  setNote: (n: CreateNoteParams | undefined) => void;
  installations: InstallationType[];
  setInstallations: React.Dispatch<React.SetStateAction<InstallationType[]>>;
  installationsOptions: InstallationType[] | null;
  setInstallationsOptions: React.Dispatch<React.SetStateAction<InstallationType[] | null>>;
  groupedParts: GroupedTiresType[];
  setGroupedParts: React.Dispatch<React.SetStateAction<GroupedTiresType[]>>;
  mountingOptionsModalOpen: UFOPart | null;
  setMountingOptionsModalOpen: React.Dispatch<React.SetStateAction<UFOPart | null>>;
  resetValues: () => void;
  fittingTires: string[];
  setFittingTires: React.Dispatch<React.SetStateAction<string[]>>;
  disableCreateOrder: boolean | null;
  setDisableCreateOrder: React.Dispatch<React.SetStateAction<boolean>>;
  removePartFromGroup: (mspn:string) => void;
};
export const WizardContext = React.createContext<WizardContextType>({} as WizardContextType);
export function useWizardContext() {
  const [mobileAddress, setMobileAddress] = useState<MobileAddress | null>(null);
  const [vehicle, setVehicle] = useState<VehicleType | null>(null);
  const [selectedTires, setSelectedTires] = useState<SelectedTire[]>([]);
  const [selectedServices, setSelectedServices] = useState<SelectedServicesType[]>([]);
  const [provider, setProvider] = useState<LocationCore | null>(null);
  const [fleet, setFleet] = useState<LocationCore | null>(null);
  const [date, setDate] = useState<Date | null>(null);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [authorized, setAuthorized] = useState<boolean>(true);
  const [appointmentType, setAppointmentType] = useState<AppointmentType | null>(null);
  const [appointmentSelected, setAppointmentSelected] = useState<boolean>(false);
  const [payment, setPayment] = useState<PaymentType | null>(null);
  const [showAppointmentSelector, setShowAppointmentSelector] = useState<boolean>(false);
  const [customReq, setCustomReq] = useState<{ text: string; selected: boolean }>({ text: '', selected: false });
  const [billingPreferences, setBillingPreferences] = useState<BillingPreferencesType>();
  const [pqlPrices, setPqlPrices] = useState<{ mspn: string; price: number; line: number; tireMspn: string }[]>([]);
  const [showCartModal, setShowCartModal] = useState(false);
  const [totalPrice, setTotalPrice] = useState<number>();
  const [deliveryTaxes, setDeliveryTaxes] = useState<{ taxes: TaxesType; total: number }>();
  const [note, setNote] = useState<CreateNoteParams>();
  const { loading: createOrderLoading, create } = useOrderCreate();
  const { loading: submitOrderLoading, callOrderAction } = useOrderActions();
  const { loading: addPArtLoading, addParts } = useAddPartsToOrder();
  const [installations, setInstallations] = useState<InstallationType[]>([]);
  const [installationsOptions, setInstallationsOptions] = useState<InstallationType[] | null>(null);
  const [groupedParts, setGroupedParts] = useState<GroupedTiresType[]>([]);
  const [mountingOptionsModalOpen, setMountingOptionsModalOpen] = useState<UFOPart | null>(null);
  const [fittingTires, setFittingTires] = useState<string[]>([]);
  const loading = createOrderLoading || addPArtLoading || submitOrderLoading || false;
  const history = useHistory();
  const userId = useContext<CentralProviderState>(CentralProviderContext).centralProfile?.uid;
  const isDealer = useCurrentLocationIsDealer();
  const googleAnalyticsUtils = useGoogleAnalytics();
  const [disableCreateOrder, setDisableCreateOrder] = useState<boolean>(false);

  const { selectedGrantLocation } = useContext<CentralProviderState>(CentralProviderContext);
  const formattedSelectedGrantLocation = formatAsLocationCoreType(selectedGrantLocation);

  const snackbar = useEnqueueSnackbar();

  const resetValues = () => {
    setSelectedTires([]);
    setSelectedServices([]);
    setDate(null);
    setPayment(null);
    setAppointmentSelected(false);
    setMobileAddress(null);
    setAppointmentType(null);
    setPqlPrices([]);
    setNote(undefined);
    setInstallations([]);
    setInstallationsOptions(null);
    setGroupedParts([]);
    setDisableCreateOrder(false);
  };

  useEffect(() => {
    resetValues();
  }, [vehicle]);

  useEffect(() => {
    if (showCartModal) {
      googleAnalyticsUtils.event({
        category: AnalyticsCategory.ORDER_CREATION,
        action: AnalyticsAction.USER_OPEN_SHOPPING_CART,
        isDealer: !!isDealer,
      });
    }
  }, [showCartModal, googleAnalyticsUtils, isDealer]);

  useEffect(() => {
    if (selectedGrantLocation && !isDealer) setFleet(formattedSelectedGrantLocation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGrantLocation]);

  useEffect(() => {
    if (installationsOptions) {
      setInstallations(installationsOptions);
    } else {
      let updatedInstallations: InstallationType[] = JSON.parse(JSON.stringify(installations));
      selectedTires.forEach((t) => {
        t.part.service_mspn.forEach((smspn) => {
          updatedInstallations = updatedInstallations.map((i: InstallationType) => {
            // updates installations qty
            if (i.related_to === t.related_to && i.mspn === smspn && i.quantity !== t.qty && !t.removed && !i.removed) {
              return { ...i, quantity: t.qty };
            }
            return i;
          });
          if (
            !t.removed &&
            !updatedInstallations.some((i: InstallationType) => i.related_to === t.related_to && i.mspn === smspn)
          ) {
            updatedInstallations.push({
              // Add installations to new tires
              related_to: t.related_to,
              quantity: t.qty,
              mspn: smspn,
              removed: false,
            });
          }
        });
      });
      // remove installation of the removed tires
      const remainingTires = selectedTires.filter((st) => !st.removed).map((st) => st.related_to);
      updatedInstallations = updatedInstallations.map((i: InstallationType) => {
        if (remainingTires.includes(i.related_to)) return { ...i, removed: false };
        return { ...i, removed: true };
      });
      setInstallations(updatedInstallations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTires, installationsOptions]);
  const { t } = useTranslation();
  const { PATH_ORDER_DETAIL_TO } = useUFOPaths();

  const displayErrorDialog = () => {
    snackbar.enqueueMessages({
      message: t('There was an error creating the order. Please try again in a few minutes.'),
      color: 'danger',
    });
  };

  const displayAdminApprovalMessage = (orderId: string | number) => {
    history.push(PATH_ORDER_DETAIL_TO(orderId))
    snackbar.enqueueMessages({
      message: t(
          "Order {{orderId}} exceeds the fleet's spending threshold and has been submitted to a fleet admin for approval.",
          { orderId },
        ),
      color: 'warning',
      persist: true,
    });
  };

  const displayPqlPricesReqMessage = (orderId: string | number) => {
    history.push(PATH_ORDER_DETAIL_TO(orderId))
    snackbar.enqueueMessages({
      message: t(
          'Order {{orderId}} contains services with pricing that must be provided by the service provider. Once provided, the order will be sent to you for your review and any required approvals.  When the order is submitted, it will Allow an override of this price by local dealer in the order creation modal.',
          { orderId },
        ),
      color: 'info',
      persist: true,
    });
  };

  const displayProviderApprovalMessage = (orderId: string | number) => {
    history.push(PATH_ORDER_DETAIL_TO(orderId))
    snackbar.enqueueMessages({
      message: t('Order {{orderId}} has been submitted to a provider for approval.', { orderId }),
      color: 'info',
      persist: true,
    });
  };

  const displaySuccessfulMessage= (orderId: string | number) => {
    history.push(PATH_ORDER_DETAIL_TO(orderId))
    snackbar.enqueueMessages({
      message: t('Order {{orderId}} was created.', { orderId }),
      color: 'success',
    });
  };

  const getCommonPricesParams = () => {
    const params = {
      mode: isDealer ? 'provider' : ('fleet' as LocationType),
      provider_id: provider?.customer_number || '',
      fleet_id: fleet?.customer_number || '',
      country_code: (provider?.customer_country as CountriesType) || 'US', // tires endpoint needs country code on upper
      state: mobileAddress?.state || undefined,
      city: mobileAddress?.city || undefined,
      zip_code: mobileAddress?.zip || undefined,
    };
    return params;
  };

  const setPql = (mspn: string, price: number, line: number, tireMspn: string) => {
    const mutablePqlPrices = cloneDeep(pqlPrices);
    const founded = mutablePqlPrices?.findIndex((p) => p.mspn === mspn && p.line === line && p.tireMspn === tireMspn);
    if (founded >= 0) {
      mutablePqlPrices[founded] = {
        mspn,
        price,
        line,
        tireMspn,
      };
    } else {
      mutablePqlPrices.push({
        mspn,
        price,
        line,
        tireMspn,
      });
    }
    setPqlPrices(mutablePqlPrices);
  };

  const getPql = (mspn: string, line: number, tireMspn: string) => {
    if (pqlPrices) {
      const founded = pqlPrices.find((p) => p.mspn === mspn && p.line === line && p.tireMspn === tireMspn);
      if (founded) return founded.price;
    }
    return null;
  };
  const getServices = (withCustom?: boolean) => {
    const services: {
      part_id: string;
      quantity: number;
      comment?: string;
      part_description: string;
      related_to?: string;
      ispql: boolean;
      dealer_part_id?: string;
      manual_pricing?: boolean;
      invoice_price?: number;
      final_invoice_price?: number;
      removed?: boolean;
      line: number;
      isInstallation?: boolean;
      custom_tire?: boolean;
    }[] = [];
    selectedServices
      .filter((s) => s.part.mspn)
      .forEach((s) => {
        s.lines
          .filter((l) => l.selected || l.removed)
          .forEach((l, lineIndex) => {
            const findPrices = pqlPrices?.find((p) => (p.mspn === getMspn(s.part) && p.line === lineIndex) || '');
            const price = findPrices?.price;
            services.push({
              part_id: s.part.mspn,
              quantity: l.quantity,
              comment: l.comment,
              part_description: s.part.description,
              related_to: l.related_to,
              dealer_part_id: s.part.dealer_part_id,
              ispql: s.part.ispql || false,
              manual_pricing: (s.part.ispql && !!price) || undefined,
              invoice_price: price,
              final_invoice_price: price ? price * l.quantity : undefined,
              removed: l.removed,
              line: lineIndex,
            });
          });
      });
    const finalServices: {
      part_id: string;
      quantity: number;
      comment?: string;
      part_description: string;
      related_to?: string;
      ispql: boolean;
      manual_pricing?: boolean;
      invoice_price?: number;
      final_invoice_price?: number;
      removed?: boolean;
      isInstallation?: boolean;
      custom_tire?: boolean;
    }[] = [];
    if (selectedTires.length > 0) {
      selectedTires.forEach((st) => {
        if (customReq.selected && withCustom) {
          finalServices.push({
            part_id: 'custom_request',
            quantity: 1,
            comment: customReq.text,
            part_description: 'Custom Requirement',
            related_to: st.related_to,
            ispql: st.part.ispql || false,
          });
        }
      });
    }
    const installationsPayload = installations.filter((i) => !i.removed).map((i) => {
      const findPrices = pqlPrices?.find((p) => p.mspn === PQL_MOUNTING_WHEEL || '');
      const price = findPrices?.price;
      return {
        part_id: i.mspn,
        quantity: i.quantity,
        comment: i.comment,
        part_description: '',
        related_to: i.related_to,
        ispql: i.isPql || false,
        invoice_price: price,
        final_invoice_price: price ? price * i.quantity : undefined,
        manual_pricing: i.isPql || false,
        isInstallation: true,
        removed: i.removed,
      };
    });
    return [...installationsPayload, ...finalServices, ...services];
  };
  const getPartAndServices = () => {
    let partsAndServices = getServices();
    if (selectedTires.length > 0) {
      // Add tires
      selectedTires.forEach((st) => {
        partsAndServices = insertElement<typeof partsAndServices[number]>(0, partsAndServices, {
          part_id: st.part.mspn || '',
          quantity: st.qty || 0,
          part_description: st.part.description || '',
          ispql: st.part.ispql || false,
          removed: st.removed,
          related_to: st.related_to,
          custom_tire: !fittingTires.includes(st.part.mspn),
        });
      });
    }
    return partsAndServices;
  };

  const getItemsQty = () =>
    getPartAndServices().reduce((count, part) => {
      if (!part.isInstallation && !part.removed) return count + part.quantity;
      return count;
    }, 0);

  const enterInfoLater = payment?.payment_type === CONTINUE_WITHOUT_PAYMENT_METHOD_OPTION;

  const getPoNumber = () => {
    if (enterInfoLater) return undefined;
    if (!payment?.po_number) return undefined;
    if (
      checkMasks({
        str: payment.po_number,
        mask: billingPreferences?.po?.mask,
        maskAlt: billingPreferences?.po?.alternate_mask,
        max: 15,
      }).severity
    )
      return undefined;
    return payment.po_number;
  };
  const getRefNumber = () => {
    if (enterInfoLater) return undefined;
    if (!payment?.reference_number) return undefined;
    if (
      checkMasks({
        str: payment.reference_number,
        mask: billingPreferences?.fleet_reference_number?.mask,
        maskAlt: billingPreferences?.fleet_reference_number?.alternate_mask,
      }).severity
    )
      return undefined;
    return payment.reference_number;
  };

  const createOrder = () => {
    if (!provider || !fleet || !payment) return;
    let notePayload = note;
    if (!notePayload && customReq?.selected) {
      notePayload = {
        description: customReq.text,
        note_type: 'quote',
        template: false,
        event_type: 'custom_request',
      };
    }
    create({
      mobile_address: mobileAddress?.validated
        ? { ...mobileAddress, address: `${mobileAddress?.streetNumber ?? ''} ${mobileAddress?.street ?? ''}` }
        : undefined,
      contact_id: userId,
      provider_id: provider?.customer_number || provider?.customer_number,
      vehicle_id: vehicle ? `${vehicle.id}` : undefined,
      provider_location_id: provider?.customer_number || provider?.customer_number,
      orderer_id: fleet?.customer_number,
      orderer_location_id: fleet?.customer_number,
      pickup_id: provider?.customer_number || provider?.customer_number,
      delivery_id: fleet?.customer_number,
      delivery_method: 'pickup',
      order_type: 'urban',
      appointment: (!isDealer || appointmentSelected) && date ? date.toISOString() : undefined,
      vehicle_type: 'urban',
      credit_card_token: payment.credit_card_token || undefined,
      credit_card_last_four: payment.credit_card_last_four || undefined,
      authorization_number: payment.authorization_number || undefined,
      po_number: getPoNumber(),
      reference_number: getRefNumber(),
      payment_type: payment.payment_type || undefined,
      appointment_type: appointmentType || undefined,
    })
      .then((orderResponse) => {
        if (!orderResponse.data?.order?.id) {
          displayErrorDialog();
          return;
        }
        addParts(
          {
            order: {
              action: { edit: { message: null } },
              parts: getPartAndServices().filter((s) => !s.removed),
            },
            perform_now: true,
          },
          orderResponse.data?.order?.id,
        )
          .then((addPartResponse) => {
            if (!addPartResponse.data?.order?.id) {
              displayErrorDialog();
              return;
            }
            callOrderAction(
              {
                order: { action: { submit: { message: null } } },
                note: notePayload,
                perform_now: true,
              },
              addPartResponse.data?.order?.id,
            )
              .then((submitResponse) => {
                if (!submitResponse?.data?.order)
                  throw new Error('An unexpected response was returned from the callOrderAction');
                if (submitResponse.data?.order?.id) {
                  if (!isDealer && submitResponse.data?.order?.parts.some((p) => p.pql_pricing && !p.invoice_price)) {
                    googleAnalyticsUtils.event({
                      category: AnalyticsCategory.ORDER_CREATION,
                      action: AnalyticsAction.CREATED_WITH_PQL_PARTS,
                      isDealer: !!isDealer,
                      orderType: appointmentType,
                    });
                    displayPqlPricesReqMessage(submitResponse.data.order.id as string | number);
                    return;
                  }
                  if (submitResponse.data.order.status.primary.includes('admin')) {
                    googleAnalyticsUtils.event({
                      category: AnalyticsCategory.ORDER_CREATION,
                      action: AnalyticsAction.CREATED_ADMIN_APPROVAL_NEEDED,
                      isDealer: !!isDealer,
                      orderType: appointmentType,
                    });
                    displayAdminApprovalMessage(submitResponse.data.order.id as string | number);
                    return;
                  }
                  if (!isDealer) {
                    googleAnalyticsUtils.event({
                      category: AnalyticsCategory.ORDER_CREATION,
                      action: AnalyticsAction.CREATE_PROVIDER_APPROVAL_NEEDED,
                      isDealer: !!isDealer,
                      orderType: appointmentType,
                    });
                    displayProviderApprovalMessage(submitResponse.data.order.id as string | number);
                    return;
                  }

                  googleAnalyticsUtils.event({
                    category: AnalyticsCategory.ORDER_CREATION,
                    action: AnalyticsAction.CREATED,
                    isDealer: !!isDealer,
                    orderType: appointmentType,
                  });
                  displaySuccessfulMessage(submitResponse.data.order.id as string | number);
                } else {
                  displayErrorDialog();
                }
              })
              .catch((_) => {
                // TODO: better error handling
                console.error('An error occurred in the order submission');
              });
          })
          .catch((_) => {
            // TODO: better error handling
            console.error('An error occurred in the add part request');
          });
      })
      .catch((_) => {
        // TODO: better error handling
        console.error('An error occurred in the order creation request');
      });
  };

  const removePartFromGroup = (mspn: string) => {
    setGroupedParts((prev) => prev.filter((part) => part.tireMspn !== mspn))
    const tire = selectedTires.find((ti) => ti.part.mspn === mspn);
    if (tire) setInstallationsOptions((prev) => prev ? prev.map((inst) => {
      if (inst.related_to === tire.related_to) return {...inst, removed: true};
      return inst;
    }
    ) : null)
  }

  return {
    customReq,
    setCustomReq,
    mobileAddress,
    setMobileAddress,
    appointmentSelected,
    setAppointmentSelected,
    showAppointmentSelector,
    setShowAppointmentSelector,
    selectedServices,
    setSelectedServices,
    selectedTires,
    setSelectedTires,
    vehicle,
    setVehicle,
    provider,
    setProvider,
    fleet,
    setFleet,
    date,
    setDate,
    disabled,
    setDisabled,
    payment,
    setPayment,
    authorized,
    setAuthorized,
    billingPreferences,
    setBillingPreferences,
    appointmentType,
    setAppointmentType,
    contextPqlPrices: pqlPrices,
    setContextPqlPrices: setPqlPrices,
    showCartModal,
    setShowCartModal,
    totalPrice,
    setTotalPrice,
    deliveryTaxes,
    setDeliveryTaxes,
    note,
    setNote,
    loading,
    createOrder,
    getServices,
    getPartAndServices,
    setPql,
    getPql,
    getCommonPricesParams,
    getItemsQty,
    installations,
    setInstallations,
    installationsOptions,
    setInstallationsOptions,
    groupedParts,
    setGroupedParts,
    mountingOptionsModalOpen,
    setMountingOptionsModalOpen,
    resetValues,
    fittingTires,
    setFittingTires,
    disableCreateOrder,
    setDisableCreateOrder,
    removePartFromGroup,
  };
}
